import type { UpdatePrivateEventInput } from '../../api/private-event-update';
import type { PrivateEventDetail } from '../../api/privateEvents';

import { isValidEmail, isValidUsPhone, parseDealValueCents } from './private-event-fields';
import type { StepErrors } from './private-event-validation';

export interface ClientDealDraft {
  clientName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  /** Dollars as typed. */
  dealValue: string;
  hubspotDealId: string;
  depositReceived: boolean;
  hubspotPaymentId: string;
  ownerUserId: string;
  nationalCoordinatorUserId: string;
  dealOriginatorUserId: string;
  /** Display names for the picked people; never sent. */
  ownerName: string;
  nationalCoordinatorName: string;
  dealOriginatorName: string;
}

const MAX_INT = 2_147_483_647;

const blank = (value: string | null) => value ?? '';

/** Whole dollars as "2400", anything else with both cent digits, "2400.50". */
function seedDealValue(cents: number | null): string {
  if (cents === null) return '';
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
}

export function seedClientDeal(event: PrivateEventDetail): ClientDealDraft {
  return {
    clientName: event.clientName,
    contactName: blank(event.contactName),
    contactEmail: blank(event.contactEmail),
    contactPhone: blank(event.contactPhone),
    dealValue: seedDealValue(event.dealValueCents),
    hubspotDealId: blank(event.hubspotDealId),
    depositReceived: event.depositReceived,
    hubspotPaymentId: blank(event.hubspotPaymentId),
    ownerUserId: event.owner?.id ?? event.ownerUserId ?? '',
    nationalCoordinatorUserId: event.nationalCoordinator?.id ?? '',
    dealOriginatorUserId: event.dealOriginator?.id ?? '',
    ownerName: event.owner?.name ?? '',
    nationalCoordinatorName: event.nationalCoordinator?.name ?? '',
    dealOriginatorName: event.dealOriginator?.name ?? '',
  };
}

type TextKey = 'clientName' | 'contactName' | 'contactEmail' | 'contactPhone' | 'hubspotDealId';

function textChanges(seed: ClientDealDraft, now: ClientDealDraft): UpdatePrivateEventInput {
  const patch: Record<string, string | null> = {};
  const keys: TextKey[] = [
    'clientName',
    'contactName',
    'contactEmail',
    'contactPhone',
    'hubspotDealId',
  ];
  for (const key of keys) {
    if (now[key].trim() !== seed[key].trim()) patch[key] = now[key].trim() || null;
  }
  if (patch.clientName === null) delete patch.clientName;
  return patch;
}

function peopleChanges(seed: ClientDealDraft, now: ClientDealDraft): UpdatePrivateEventInput {
  const patch: UpdatePrivateEventInput = {};
  if (now.ownerUserId !== seed.ownerUserId && now.ownerUserId) patch.ownerUserId = now.ownerUserId;
  if (now.nationalCoordinatorUserId !== seed.nationalCoordinatorUserId) {
    patch.nationalCoordinatorUserId = now.nationalCoordinatorUserId || null;
  }
  if (now.dealOriginatorUserId !== seed.dealOriginatorUserId) {
    patch.dealOriginatorUserId = now.dealOriginatorUserId || null;
  }
  return patch;
}

/** Unticking sends only the flag: the server clears the payment id with it. */
function depositChanges(seed: ClientDealDraft, now: ClientDealDraft): UpdatePrivateEventInput {
  const patch: UpdatePrivateEventInput = {};
  if (now.depositReceived !== seed.depositReceived) patch.depositReceived = now.depositReceived;
  const paymentMoved = now.hubspotPaymentId.trim() !== seed.hubspotPaymentId.trim();
  const ticked = now.depositReceived && !seed.depositReceived;
  if (now.depositReceived && (paymentMoved || (ticked && now.hubspotPaymentId.trim()))) {
    patch.hubspotPaymentId = now.hubspotPaymentId.trim() || null;
  }
  return patch;
}

export function clientDealPatch(
  seed: ClientDealDraft,
  now: ClientDealDraft,
): UpdatePrivateEventInput {
  const patch = {
    ...textChanges(seed, now),
    ...peopleChanges(seed, now),
    ...depositChanges(seed, now),
  };
  const cents = parseDealValueCents(now.dealValue);
  if (cents !== undefined && cents !== parseDealValueCents(seed.dealValue)) {
    patch.dealValueCents = cents;
  }
  return patch;
}

function contactErrors(event: PrivateEventDetail, d: ClientDealDraft): StepErrors {
  const errs: StepErrors = {};
  const email = d.contactEmail.trim();
  if (email !== blank(event.contactEmail).trim() && email && !isValidEmail(email)) {
    errs.contactEmail = 'Enter a valid email address.';
  }
  const phone = d.contactPhone.trim();
  if (phone !== blank(event.contactPhone).trim() && phone && !isValidUsPhone(phone)) {
    errs.contactPhone = 'Enter a US phone number, like (555) 123-4567.';
  }
  return errs;
}

function dealErrors(d: ClientDealDraft): StepErrors {
  const errs: StepErrors = {};
  const cents = parseDealValueCents(d.dealValue);
  if (cents === undefined) errs.dealValueCents = 'Enter an amount, like 2,400.';
  else if (cents !== null && cents > MAX_INT) errs.dealValueCents = 'That amount is too large.';
  return errs;
}

/** Contact formats are checked only when changed, so an odd imported value
 *  never blocks an unrelated edit. */
export function clientDealErrors(event: PrivateEventDetail, d: ClientDealDraft): StepErrors {
  const errs = { ...contactErrors(event, d), ...dealErrors(d) };
  if (d.clientName.trim() === '') errs.clientName = 'Enter the client name.';
  return errs;
}
